import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  DoNotDisturbOnOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  MoreVertOutlined,
} from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import type { PayFileInvoiceLineFields } from "../hooks/usePayFileInvoiceColumns"
import { useRemovePayFileInvoice } from "../hooks/useRemovePayFileInvoice"

import { PayFileInvoiceRemoveConfirmationDialog } from "./PayFileInvoiceRemoveConfirmationDialog"

interface PayFileInvoiceRowActionsProps {
  row: CellContext<PayFileInvoiceLineFields, unknown>["row"]
  payFileId: string
  canRemove: boolean
  onSuccess: () => void
}

export const PayFileInvoiceRowActions: FC<PayFileInvoiceRowActionsProps> = ({
  row,
  payFileId,
  canRemove,
  onSuccess,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = useState(false)
  const { removePayFileInvoice, isRemoving } = useRemovePayFileInvoice()

  const { invoice } = row.original

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const handleRemove = () => {
    handleMenuClose()
    setIsRemoveConfirmationOpen(true)
  }

  const handleRemoveConfirm = async (comment: string) => {
    const isSuccess = await removePayFileInvoice({ payFileId, invoiceId: invoice.invoiceId, comment })

    if (!isSuccess) return

    setIsRemoveConfirmationOpen(false)
    onSuccess()
  }

  return (
    <Box className='flex items-center justify-end gap-x-3' ref={anchorElRef}>
      {canRemove && (
        <>
          <IconButton
            size='small'
            onClick={handleMenuOpen}
            data-testid={`pay-file.invoice-row-actions.menu-button-${invoice.invoiceId}`}
          >
            <MoreVertOutlined />
          </IconButton>
          <Menu
            anchorEl={anchorElRef.current}
            open={isMenuOpen}
            onClose={handleMenuClose}
            data-testid={`pay-file.invoice-row-actions.menu-${invoice.invoiceId}`}
          >
            <MenuItem
              onClick={handleRemove}
              data-testid={`pay-file.invoice-row-actions.remove-button-${invoice.invoiceId}`}
            >
              <ListItemIcon>
                <DoNotDisturbOnOutlined />
              </ListItemIcon>
              <ListItemText primary='Remove' />
            </MenuItem>
          </Menu>
          {isRemoveConfirmationOpen && (
            <PayFileInvoiceRemoveConfirmationDialog
              isSubmitting={isRemoving}
              onClose={() => setIsRemoveConfirmationOpen(false)}
              onConfirm={(comment) => void handleRemoveConfirm(comment)}
            />
          )}
        </>
      )}
      <IconButton
        size='small'
        onClick={row.getToggleExpandedHandler()}
        data-testid={`pay-file.invoice-row-actions.expand-button-${invoice.invoiceId}`}
      >
        {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
      </IconButton>
    </Box>
  )
}
